import { ReactElement, ReactNode, useMemo, useState } from "react";
import { useSelector, useStore } from "react-redux";
import { Direction, PageSet } from "src/pageDefinitions/types";
import {
  getPageSet,
  getPageSetForContext,
  getPage,
  getPageAtLocation,
} from "src/pageDefinitions/pageSets";
import { CoreReduxState } from "@utils/redux/store";

import goto from "src/pageDefinitions/goto";

import {
  ensureGotoSetup,
  gotoPage,
  GoToPageOptions,
} from "src/pageDefinitions/goto/pageSet";
import { useAsyncError } from "./error";
import { useHistoryHack } from "./historyHack";
import { preloadPagePath } from "src/router";
import { useCallbackRef, useOnce } from "./lifecycle";
import { captureException } from "src/utils/sentry";
import {
  PageSetEventHandler,
  pageSetEvents,
} from "src/pageDefinitions/pageSets/bus";
import {
  findFuturePageIndex,
  resolvePage,
} from "src/pageDefinitions/pageSets/eval";
import { setActivePageSet } from "src/utils/redux/slices/linearBuyflow";
import { resolveParamPageSet } from "src/pageDefinitions/pageSets/init";
import { useAppDispatch } from "./redux";

/**
 * Blocks until we are able to fully load the current pageset
 */
export function PageSetGate({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const [loaded, setLoaded] = useState(false);
  const state = useSelector((select: CoreReduxState) => select);

  useOnce(() => {
    const paramPageSet = resolveParamPageSet(state);
    if (paramPageSet && paramPageSet.id !== state.linearBuyflow.pageSetName) {
      dispatch(setActivePageSet(paramPageSet));
    }
    setLoaded(true);
  });

  if (!loaded) {
    return null;
  }

  return children as ReactElement;
}

export function useGotoOptions(): GoToPageOptions {
  // TODO: Convert after we kill off hash routers
  const history = useHistoryHack(); // useHistory();
  const dispatch = useAppDispatch();
  const store = useStore<CoreReduxState>();
  return useMemo(
    () => ({
      history,
      dispatch,
      store,
    }),
    [history, dispatch, store]
  );
}

// TODO: Heavily document this. Why would you use it. What gotchas are there?
export function usePageSetEvents(cb: PageSetEventHandler) {
  const cbRef = useCallbackRef(cb);

  return useOnce(() => {
    pageSetEvents.add(cbRef);

    return () => {
      pageSetEvents.delete(cbRef);
    };
  });
}

function preloadFuturePage(
  gotoOptions: GoToPageOptions & {
    direction: Direction;
    activePageSet: PageSet;
  }
) {
  const { direction, activePageSet, store } = gotoOptions;
  const futurePageIndex = findFuturePageIndex(
    direction,
    activePageSet,
    gotoOptions
  );
  const futurePage = getPageAtLocation(activePageSet, futurePageIndex);
  if (futurePage) {
    const resolvedPage = resolvePage(futurePage, store.getState(), direction);

    if (resolvedPage && "pathname" in resolvedPage && resolvedPage.pathname) {
      preloadPagePath(resolvedPage.pathname);
    }
  }
}

pageSetEvents.add((event, eventParams) => {
  if (event === "after:goto") {
    preloadFuturePage(eventParams);
  }
});

export function usePageSetPreload(context: string) {
  const gotoOptions = useGotoOptions();
  const state = useSelector((param: CoreReduxState) => param);

  useOnce(() => {
    const pageSet = getPageSetForContext(context, state);
    if (pageSet) {
      preloadFuturePage({
        direction: Direction.FORWARD,
        activePageSet: pageSet,
        ...gotoOptions,
      });
    }
  });
}

/**
 * Linear buyflow framework that wraps pool pages and handles users moving forward
 * and backwards.
 */
export function usePageSet() {
  const gotoOptions = useGotoOptions();

  const activePageSetName = useSelector(
    (state: CoreReduxState) => state.linearBuyflow.pageSetName
  );

  const activePageSet = getPageSet(activePageSetName);
  const activePage = getPage(
    activePageSet?.pages,
    gotoOptions.history.location.pathname,
    gotoOptions.store.getState()
  );

  useOnce(() => {
    ensureGotoSetup(activePageSet, activePage, gotoOptions);

    if (!activePageSet) {
      captureException(new Error("Page set not found"), "usePageSet");

      // eslint-disable-next-line no-debugger
      debugger;

      goto.landing();
    }
  });

  const asyncError = useAsyncError();

  return useMemo(
    () => ({
      activePage,
      activePageSet,

      gotoNextPage: async (replace?: boolean) =>
        gotoPage(Direction.FORWARD, activePageSet, gotoOptions, replace).catch(
          asyncError
        ),
      gotoPrevPage: async (replace?: boolean) =>
        gotoPage(Direction.BACKWARD, activePageSet, gotoOptions, replace).catch(
          asyncError
        ),
    }),
    [activePage, activePageSet, gotoOptions, asyncError]
  );
}
